import styles from "./DatePicker.module.scss";
import React, {useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import clsx from "clsx";
import Form from "react-bootstrap/esm/Form";
import ClickAwayListener from "react-click-away-listener";

export const DatePicker = ({value, onChange, isInvalid, error}) => {
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [calendarDate, setCalendarDate] = useState(() => (value ? moment(value) : moment()).startOf("month"));

  const [inputValue, setInputValue] = useState(value ? moment(value).format("DD/MM/YYYY") : "");

  const inputRef = useRef(null);

  useEffect(() => {
    if (value) {
      setCalendarDate(moment(value).startOf("month"));
      setInputValue(moment(value).format("DD/MM/YYYY"));
    }
  }, [value]);

  const now = moment();

  const days = useMemo(() => {
    const d = [];

    const currentDate = calendarDate.clone().startOf("isoWeek");

    for (let i = 0; i < 42; i++) {
      d.push(currentDate.clone());

      currentDate.add(1, "day");
    }

    return d;
  }, [calendarDate]);

  const chooseDate = date => {
    if (date) {
      setInputValue(date.format("DD/MM/YYYY"));

      onChange(date.format("YYYY-MM-DD"));
    } else {
      setInputValue("");

      onChange(null);
    }
  };

  const currentValue = /^\d\d\/\d\d\/\d\d\d\d$/.test(inputValue) ? moment(inputValue, "DD/MM/YYYY") : null;

  return <div className={styles.container}>
    <Form.Control
      className={styles.input}
      type="text"
      value={inputValue}
      onChange={e => {
        setInputValue(e.target.value);

        if (/^\d\d\/\d\d\/\d\d\d\d$/.test(e.target.value)) {
          const value = moment(e.target.value, "DD/MM/YYYY");

          setCalendarDate(value.clone().startOf("month"));

          onChange(value.format("YYYY-MM-DD"));
        } else {
          onChange(null);
        }
      }}
      maxLength="255"
      onFocus={() => setDatePickerVisible(true)}
      onMouseDown={() => setDatePickerVisible(true)}
      onBlur={e => {
        if (!/^\d\d\/\d\d\/\d\d\d\d$/.test(e.target.value)) {
          chooseDate(null);
        }
      }}
      ref={inputRef}
      isInvalid={isInvalid}
    />

    <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>

    {datePickerVisible && <ClickAwayListener onClickAway={e => {
      if (e.target !== inputRef.current) {
        setDatePickerVisible(false);
      }
    }}>
      <div className={clsx(styles.datePicker, datePickerVisible ? styles.visible : null)}>
        <div className={styles.navigator}>
          <div className={styles.navigatorItem}>
            <span className={clsx("material-icons", styles.left)}
                  onClick={() => setCalendarDate(calendarDate.clone().subtract(1, "month"))}>
              chevron_left
            </span>

            <span className={styles.navigatorValue}>{calendarDate.format("MMM")}</span>

            <span className={clsx("material-icons", styles.right)}
                  onClick={() => setCalendarDate(calendarDate.clone().add(1, "month"))}>
              chevron_right
            </span>
          </div>

          <div className={styles.navigatorItem}>
            <span className={clsx("material-icons", styles.left)}
                  onClick={() => setCalendarDate(calendarDate.clone().subtract(1, "year"))}>
              chevron_left
            </span>

            <span className={styles.navigatorValue}>{calendarDate.format("YYYY")}</span>

            <span className={clsx("material-icons", styles.right)}
                  onClick={() => setCalendarDate(calendarDate.clone().add(1, "year"))}>
              chevron_right
            </span>
          </div>
        </div>

        <div className={styles.days}>
          {(days || []).map(d => {
            const classes = [styles.day];

            if (d.isSame(currentValue, "day")) {
              classes.push(styles.active);
            } else if (d.isSame(now, "day")) {
              classes.push(styles.today);
            } else if (d.get("month") !== calendarDate.get("month")) {
              classes.push(styles.otherMonth);
            }

            let dateFormatted = d.format("YYYY/MM/DD");

            return <div key={dateFormatted} className={clsx(classes)} onClick={() => {
              chooseDate(d);

              setDatePickerVisible(false);
            }}>{d.format("D")}</div>;
          })}
        </div>
      </div>
    </ClickAwayListener>}
  </div>;
};
